import moment from 'moment-timezone';
import { RRule, Weekday } from 'rrule';
import { DayKey } from '../../ProfileDetailsCard/formatWorkingHours';

const RRULE_WEEKDAY_TO_DAY_KEY: DayKey[] = [
  'mon',
  'tue',
  'wed',
  'thu',
  'fri',
  'sat',
  'sun',
];

export const getWeeklyDaysFromRRule = (rruleString: string): number[] => {
  try {
    const rrule = RRule.fromString(rruleString);

    if (rrule.options.freq !== RRule.WEEKLY) {
      return [];
    }

    if (!rruleString.includes('BYDAY=')) {
      return [];
    }

    const byweekday = rrule.options.byweekday;
    if (!byweekday) {
      return [];
    }

    const daysArray = Array.isArray(byweekday) ? byweekday : [byweekday];

    return daysArray
      .map((day) => {
        if (day && typeof day === 'object' && 'weekday' in day) {
          return (day as Weekday).weekday;
        }
        return day as number;
      })
      .sort((a, b) => a - b);
  } catch {
    return [];
  }
};

export const getScheduledDaysFromRecurrence = (
  recurrence: string | null | undefined
): DayKey[] => {
  if (!recurrence) {
    return [];
  }
  return getWeeklyDaysFromRRule(recurrence)
    .map((index) => RRULE_WEEKDAY_TO_DAY_KEY[index])
    .filter((day): day is DayKey => !!day);
};

export const resolveZone = (timeZone: string | null | undefined): string =>
  timeZone && moment.tz.zone(timeZone) ? timeZone : moment.tz.guess();

export const isDateInWeeklyRecurrence = (
  recurrence: string | null | undefined,
  date: moment.Moment,
  timeZone: string | null | undefined
): boolean => {
  const scheduledDays = recurrence ? getWeeklyDaysFromRRule(recurrence) : [];
  if (scheduledDays.length === 0) {
    return false;
  }

  const zone = resolveZone(timeZone);
  const dayIndex = date.clone().tz(zone).isoWeekday() - 1;
  return scheduledDays.includes(dayIndex);
};

export const formatReservationTimeRange = (
  startTime: string,
  endTime: string | null | undefined,
  timeZone: string | null | undefined
): string => {
  const zone = resolveZone(timeZone);
  const start = moment.tz(startTime, moment.ISO_8601, true, zone);
  if (!start.isValid()) {
    return '';
  }
  const end = endTime ? moment.tz(endTime, moment.ISO_8601, true, zone) : null;
  if (!end || !end.isValid()) {
    return start.format('h:mm a');
  }
  return `${start.format('h:mm a')} - ${end.format('h:mm a')}`;
};

/** The ISO calendar day (in the reservation's zone) used to group by date. */
export const getReservationDayKey = (
  startTime: string,
  timeZone: string | null | undefined
): string => {
  const zone = resolveZone(timeZone);
  const start = moment.tz(startTime, moment.ISO_8601, true, zone);
  return start.isValid() ? start.format('YYYY-MM-DD') : startTime;
};

/**
 * A date + time-range label for a flex reservation in the fullscreen Desks
 * panel, e.g. "Today, 9:00 am - 5:30 pm" or "Tue, Apr 7, 9:00 am - 5:30 pm".
 * The date prefix is dropped when the reservation is today (the caller passes
 * the localized "Today" word).
 */
export const formatReservationDateTimeLabel = (
  startTime: string,
  endTime: string | null | undefined,
  timeZone: string | null | undefined,
  todayLabel: string,
  now: moment.Moment = moment()
): string => {
  const zone = resolveZone(timeZone);
  const start = moment.tz(startTime, moment.ISO_8601, true, zone);
  const range = formatReservationTimeRange(startTime, endTime, timeZone);
  if (!start.isValid()) {
    return range;
  }
  const datePrefix = start.isSame(now.clone().tz(zone), 'day')
    ? todayLabel
    : start.format('ddd, MMM D');
  return range ? `${datePrefix}, ${range}` : datePrefix;
};
